/**
 * Country card for bracket matchup display.
 * Similar to GroupCard but for A vs B selection instead of star rating.
 */

import { motion } from 'framer-motion';
import { Check, Crown, Info } from 'lucide-react';
import { useState } from 'react';
import { CountryInfoModal } from '@/components/CountryInfoModal';
import type { CountryCode } from '@/data/countries/types';
import { useCountryDisplayData } from '@/hooks/useCountryDisplayData';
import { cn } from '@/lib/utils';
import { CountryLabel } from './CountryLabel';

interface MatchCardProps {
  code: CountryCode;
  onSelect: () => void;
  isWinner?: boolean;
  disabled?: boolean;
}

export function MatchCard({
  code,
  onSelect,
  isWinner,
  disabled,
}: MatchCardProps) {
  const [showInfo, setShowInfo] = useState(false);
  const { country, images } = useCountryDisplayData(code);

  if (!country) return null;

  const thumbnailImage = images[0];

  const handleInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowInfo(true);
  };

  return (
    <>
      <motion.div
        whileHover={disabled ? undefined : { scale: 1.02 }}
        whileTap={disabled ? undefined : { scale: 0.98 }}
        className="h-full w-full"
      >
        <div
          onClick={disabled ? undefined : onSelect}
          className={cn(
            'relative rounded-xl border-2 overflow-hidden transition-all h-full flex flex-col',
            isWinner && 'border-green-500 ring-2 ring-green-500/30 shadow-lg',
            !isWinner &&
              !disabled &&
              'border-border hover:border-primary hover:shadow-lg cursor-pointer',
            disabled && !isWinner && 'opacity-50 border-muted',
          )}
        >
          {/* Image area - fills most of the card */}
          <div className="flex-1 relative overflow-hidden min-h-0">
            {thumbnailImage ? (
              <img
                src={thumbnailImage}
                alt={country.name}
                className={cn(
                  'absolute inset-0 w-full h-full object-cover transition-transform duration-300',
                  !disabled && 'group-hover:scale-105',
                )}
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-muted to-muted/50 flex items-center justify-center">
                <span className="text-6xl lg:text-8xl drop-shadow-lg">
                  {country.flag}
                </span>
              </div>
            )}

            {/* Winner badge */}
            {isWinner && (
              <div className="absolute top-2 left-2 lg:top-3 lg:left-3 flex items-center gap-1 bg-green-500 text-white px-2 py-1 lg:px-3 lg:py-1.5 rounded-full shadow-lg">
                <Crown className="w-3 h-3 lg:w-4 lg:h-4" />
                <span className="text-xs lg:text-sm font-semibold">
                  Selected
                </span>
              </div>
            )}

            {/* Info icon - top right */}
            <button
              onClick={handleInfoClick}
              className="absolute top-2 right-2 lg:top-3 lg:right-3 p-1.5 lg:p-2 rounded-full bg-black/50 text-white/90 hover:bg-black/70 transition-colors"
              aria-label={`View info about ${country.name}`}
            >
              <Info className="w-4 h-4 lg:w-5 lg:h-5" />
            </button>

            {/* Gradient overlay at bottom */}
            <div className="absolute inset-x-0 bottom-0 h-20 lg:h-28 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

            {/* Country info overlay at bottom */}
            <div className="absolute bottom-0 left-0 right-0 p-2 lg:p-4">
              <CountryLabel code={code} size="lg" variant="overlay" />
            </div>
          </div>

          {/* Bottom action area - compact */}
          <div
            className={cn(
              'shrink-0 py-2 px-3 text-center transition-colors',
              isWinner ? 'bg-green-500 text-white' : 'bg-muted/50',
            )}
          >
            {isWinner ? (
              <div className="flex items-center justify-center gap-1.5">
                <Check className="w-4 h-4" />
                <span className="text-sm font-semibold">Selected</span>
              </div>
            ) : disabled ? (
              <span className="text-xs text-muted-foreground">
                Not selected
              </span>
            ) : (
              <span className="text-xs lg:text-sm font-medium text-primary">
                Tap to select
              </span>
            )}
          </div>
        </div>
      </motion.div>

      {/* Country Info Modal */}
      <CountryInfoModal
        code={code}
        open={showInfo}
        onOpenChange={setShowInfo}
        showMap={true}
      />
    </>
  );
}
